import { useState, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChannel } from "ably/react";
import { Helmet } from "react-helmet";
import Dashboard from "../../../../layouts/Dashboard";
import FlatFileUploader from "./FlatFileUploader";
import {
  Grid,
  Modal,
  PageHeader,
  PanelModal,
  Button,
  Tabs,
} from "@pushpress/shared-components";
import {
  useGetMembersData,
  useGetStripeBillingData,
  useStartPaymentMatching,
} from "../../api/migrationAssistantApi.hooks";
import PageLoadingState from "../../components/shared/PageLoadingState";
import ConsolidatedTabView from "./ConsolidatedTabView";
import MultiPaymentsDrawer from "./MultiPaymentsDrawer";
import { AppContext } from "../../../../context/app";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../utils/misc";
import {
  PAYMENT_MATCHING_COMPLETED,
  STRIPE_DATA_PULL_COMPLETED,
} from "../../constants";
import ErrorState from "../../components/shared/ErrorState";
import LoadingScreen from "./LoadingState";
import MatchingRecord from "./shared/MatchingRecord";
import { PaymentMatchingEventDataType } from "../../types";
import LoadingState from "../../components/shared/LoadingState";
import { isLocal, isDev } from "../../../../env";
import NoMatchTabView from "./NoMatchTabView";
import MatchedTabView from "./MatchedTabView";
import { useExperiment } from "../../../../hooks/useExperiment";

export default function PaymentMatching() {
  const app = useContext(AppContext);
  const queryClient = useQueryClient();
  const { useExperimentValue } = useExperiment();
  const showDataTableRefresh = useExperimentValue(
    "payment-matching-data-table-refresh",
    false,
  );

  const {
    mutate: startPaymentMatching,
    isLoading: startPaymentMatchingLoading,
  } = useStartPaymentMatching();

  const {
    data: stripeBillingData,
    isFetching: stripeBillingDataFetching,
    error: stripeBillingDataError,
  } = useGetStripeBillingData();
  const {
    data: membersData,
    isFetching: membersDataFetching,
    error: membersDataError,
    refetch: refetchMembersData,
  } = useGetMembersData();
  const [activeTab, setActiveTab] = useState(0);
  const [isMatchingModalVisible, setIsMatchingModalVisible] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [showMultiPaymentsDrawer, setShowMultiPaymentsDrawer] = useState(false);
  const [showWaitingState, setShowWaitingState] = useState(false);

  // activated users with uuid, and not sub accounts
  const eligibleForMatching =
    (Array.isArray(membersData) &&
      membersData.filter((m) => !m.parentEmail && m.userUuid)) ||
    [];

  const matchedMembers = eligibleForMatching.filter((m) => m.stripeId);
  const noMatchMembers = eligibleForMatching.filter((m) => !m.stripeId);

  const showMatchedTab = activeTab === 0;
  const showNoMatchTab = activeTab === 1;

  const allMembers = [...matchedMembers, ...noMatchMembers];

  const showInternalContent = isLocal() || isDev() || app.user.superuser;

  useChannel(`billing.${app.client.uuid}.payment.migration`, (message) => {
    const messageData: PaymentMatchingEventDataType = message?.data?.data;

    if (messageData.message === STRIPE_DATA_PULL_COMPLETED) {
      setShowWaitingState(false);
      queryClient.invalidateQueries({
        queryKey: ["fetchMigratingMembers"],
      });
      queryClient.invalidateQueries({
        queryKey: ["fetchStripeBillingData"],
      });
    } else {
      setShowWaitingState(true);
    }
  });

  useChannel(`client.${app.client.uuid}.payment.migration`, (message) => {
    const messageData: PaymentMatchingEventDataType = message?.data?.data;

    if (messageData.message === PAYMENT_MATCHING_COMPLETED) {
      setShowWaitingState(false);
      queryClient.invalidateQueries({
        queryKey: ["fetchMigratingMembers"],
      });
    } else {
      setShowWaitingState(true);
    }
  });

  const isFetching = membersDataFetching || stripeBillingDataFetching;

  const hasData = !!membersData && !!stripeBillingData;
  const showPageContent = hasData && !showWaitingState;

  const isGlobalError =
    !isFetching &&
    (membersDataError ||
      stripeBillingDataError ||
      !membersData ||
      !stripeBillingData);

  const showMatchingTable =
    !membersDataFetching && !membersDataError && !showWaitingState;

  const disableMatchingButton = eligibleForMatching.length === 0;

  return (
    <>
      <Helmet>
        <title>Payment Matching</title>
      </Helmet>
      <Dashboard scrollableContent>
        {isFetching && !hasData && <PageLoadingState />}

        {isGlobalError && <ErrorState refetch={refetchMembersData} />}

        {showWaitingState && <LoadingScreen />}

        {showPageContent && (
          <Grid container justifyContent="space-between">
            <Grid item container md={12} xs={12}>
              <Grid item md={9} xs={12}>
                <PageHeader
                  title={
                    showInternalContent
                      ? "Payment migration (internal use ONLY)"
                      : "Payment migration"
                  }
                />
              </Grid>
              {showInternalContent && (
                <Grid item md={3} xs={12} justifyContent="flex-end">
                  <FlatFileUploader
                    setShowWaitingState={setShowWaitingState}
                    isLoading={startPaymentMatchingLoading}
                  />
                  <Grid item marginY={2}>
                    {startPaymentMatchingLoading ? (
                      <LoadingState width={288} height={38} />
                    ) : (
                      <Button
                        text="Start matching"
                        size="large"
                        buttonType="secondary"
                        fullWidth
                        disabled={disableMatchingButton}
                        onClick={() => {
                          startPaymentMatching(undefined, {
                            onSuccess: (response) => {
                              if (response.allJobs === 0) {
                                toast.warning(
                                  "There were no new members to match",
                                  defaultToastOptions,
                                );
                              } else {
                                setShowWaitingState(true);
                              }
                            },
                            onError: () => {
                              toast.error(
                                "There was an error starting payment matching",
                                defaultToastOptions,
                              );
                              setShowWaitingState(false);
                            },
                          });
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>

            {
              <>
                <Grid item md={12} xs={12}>
                  <div className="tw-mb-4">
                    <h3 className="tw-text-lg tw-font-medium">
                      Payment Matching ({allMembers.length})
                    </h3>
                    <p className="tw-text-sm tw-text-gray-500">
                      Matched: {matchedMembers.length} | No Match:{" "}
                      {noMatchMembers.length}
                    </p>
                  </div>

                  {showDataTableRefresh && (
                    <ConsolidatedTabView
                      matchedMembers={matchedMembers}
                      noMatchMembers={noMatchMembers}
                      stripeBillingData={stripeBillingData?.data || {}}
                      isFetchingStripeBillingData={stripeBillingDataFetching}
                      setSelectedMemberId={setSelectedMemberId}
                      setShowMultiPaymentsDrawer={setShowMultiPaymentsDrawer}
                    />
                  )}
                </Grid>
              </>
            }

            {showMatchingTable && !showDataTableRefresh && (
              <>
                <Grid item md={12} xs={12}>
                  <Tabs
                    tabHeaders={[
                      `Matched (${matchedMembers?.length})`,
                      `No match (${noMatchMembers?.length})`,
                    ]}
                    selectedIndex={activeTab}
                    changeTab={(index) => setActiveTab(index)}
                    onTabClick={(index) => setActiveTab(index)}
                    onTabFocus={(index) => setActiveTab(index)}
                    align="left"
                    hideWithCss
                  />
                </Grid>
                <>
                  {showMatchedTab && (
                    <MatchedTabView
                      membersData={matchedMembers}
                      stripeBillingData={stripeBillingData?.data || {}}
                      setIsMatchingModalVisible={setIsMatchingModalVisible}
                      setSelectedMemberId={setSelectedMemberId}
                      setShowMultiPaymentsDrawer={setShowMultiPaymentsDrawer}
                    />
                  )}
                  {showNoMatchTab && (
                    <NoMatchTabView membersData={noMatchMembers} />
                  )}
                </>
              </>
            )}
          </Grid>
        )}
      </Dashboard>
      <PanelModal
        visible={isMatchingModalVisible}
        closeModal={() => setIsMatchingModalVisible(false)}
        disableClickAway
        disableEscapeKeyDown
        width="400px"
        verticalAlign="top"
      >
        <PanelModal.Header
          title="About this match"
          titleAlign="center"
          showCloseButton
        />
        <PanelModal.Body>
          <MatchingRecord
            stripeBillingData={stripeBillingData?.data}
            member={membersData?.find((m) => m.id === selectedMemberId) || null}
          />
        </PanelModal.Body>
        <Modal.Bottom>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12}>
              <Button
                buttonType="secondary"
                text="Close"
                size="large"
                fullWidth
                onClick={() => setIsMatchingModalVisible(false)}
              />
            </Grid>
          </Grid>
        </Modal.Bottom>
      </PanelModal>
      <MultiPaymentsDrawer
        membersData={membersData}
        selectedMemberId={selectedMemberId}
        stripeBillingData={stripeBillingData?.data}
        showMultiPaymentsDrawer={showMultiPaymentsDrawer}
        setShowMultiPaymentsDrawer={setShowMultiPaymentsDrawer}
      />
    </>
  );
}
